import {
    FocusEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    KeyboardEventHandler,
    forwardRef,
    ForwardedRef,
} from 'react';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { SuggestPicker, getSearchResultCount } from '@hh.ru/magritte-ui-suggest/SuggestPicker';
import { DataProviderError } from '@hh.ru/magritte-ui-suggest/dataProvider';
import {
    DataProviderRequest,
    DataProviderResult,
    SuggestProps,
    SuggestInputComponent,
    InputProp,
    ControlledInputProps,
    NativeFocusEventHandler,
    OptionsCount,
} from '@hh.ru/magritte-ui-suggest/types';
import { useSuggestKeyboardNav } from '@hh.ru/magritte-ui-suggest/useSuggestKeyboardNav';

import styles from './suggest.less';

const SuggestTriggerDefault = <T extends SuggestInputComponent>(props: InputProp<T> & OptionsCount): ReactElement => {
    const { component: TriggerComponent, props: triggerProps } = props;
    return (
        <TriggerComponent
            {...(triggerProps as unknown as JSX.IntrinsicAttributes &
                JSX.LibraryManagedAttributes<T, ControlledInputProps>)}
        />
    );
};

const SuggestComponent = function <K, T extends SuggestInputComponent>(
    {
        dataProvider,
        input,
        inputValue,
        onSelectValidator = () => true,
        navigationBarProps,
        maxHeight = 460,
        itemsGap = 4,
        onBlur,
        topContent,
        bottomContent,
        dropWidth,
        errorPlaceholder,
        trls,
        onChange,
        onDropOpen,
        onDropClose,
        onBottomSheetOpen,
        onBottomSheetClose,
        onSearchSubmit,
        renderTrigger = SuggestTriggerDefault,
    }: SuggestProps<K, T>,
    ref: ForwardedRef<HTMLElement>
): ReactElement | null {
    const { component: InputComponent, props: inputProps } = input;
    const [inputValueState, setInputValueState] = useState(inputProps.value || '');
    const { onChange: onInputChange, onFocus: onInputFocus, onKeyDown: onInputKeyDown } = inputProps;
    const [requestResult, setRequestResult] = useState<{ query: string; result: DataProviderResult<K> } | null>(null);
    const [valueSelectedFromSuggest, setValueSelectedFromSuggest] = useState<string | false>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLElement>(null);
    const suggestContainerRef = useRef<HTMLElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const inputRefMulti = useMultipleRefs(inputRef, ref);
    const onBlurRef = useRef<NativeFocusEventHandler | undefined>(onBlur);
    onBlurRef.current = onBlur; // update actual callback
    const { isMobile } = useBreakpoint();
    const [isError, setIsError] = useState(false);

    const currentRequestRef = useRef<DataProviderRequest>();
    const actualInputValue = inputValue === undefined ? inputValueState : inputValue;

    const { focused, onKeyDown, onFocus, ...rest } = useSuggestKeyboardNav(
        suggestContainerRef,
        inputContainerRef,
        setValueSelectedFromSuggest,
        actualInputValue,
        inputRef,
        onBlurRef
    );

    const handleBottomSheetClose = () => {
        rest.onBottomSheetClose();
        onBottomSheetClose?.();
    };

    const onChangeHandler = useCallback(
        (value: string, skipSuggestChangeHandler = false) => {
            if (inputValue === undefined) {
                setInputValueState(value);
            }
            !skipSuggestChangeHandler && onChange?.(value, false);
            onInputChange?.(value);
        },
        [inputValue, setInputValueState, onInputChange, onChange]
    );

    const requestData = useCallback(
        (query: string) => {
            setIsError(false);
            currentRequestRef.current?.cancel();
            const request = dataProvider(query);
            request.then(
                (result) => {
                    setRequestResult({ query, result });
                },
                (event) => {
                    // включаем error state если это НЕ ошибка, вызванная `minCharsCount` или отменненым запросом
                    if (!(event instanceof DataProviderError)) {
                        setIsError(true);
                    }
                    setRequestResult(null);
                }
            );
            currentRequestRef.current = request;
        },
        [dataProvider]
    );

    useEffect(() => {
        if (valueSelectedFromSuggest !== false && requestResult?.query !== actualInputValue) {
            setRequestResult(null);
        }
        if (valueSelectedFromSuggest !== actualInputValue.trim()) {
            setValueSelectedFromSuggest(false);
            if (focused) {
                requestData(actualInputValue);
            }
        }

        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [actualInputValue, requestData, focused]);

    const onFocusHandler = useCallback<FocusEventHandler<HTMLElement>>(
        (event) => {
            setValueSelectedFromSuggest(false);
            if (actualInputValue !== requestResult?.query) {
                setRequestResult(null);
                requestData(actualInputValue);
            }

            onInputFocus?.(event);
            onFocus(event);
        },
        [actualInputValue, requestResult?.query, onInputFocus, onFocus, requestData]
    );

    const onValueSelectHandler = useCallback(
        (value: string, data: K | undefined) => {
            if (!onSelectValidator(value, data)) {
                return;
            }
            if (!isMobile) {
                inputRef.current?.focus();
            }
            onChangeHandler(value, true);
            onChange?.(value, true, data);
            setValueSelectedFromSuggest(value);
        },
        [onSelectValidator, isMobile, onChangeHandler, onChange]
    );

    const onKeyDownHandler = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            onKeyDown(event);
            onInputKeyDown?.(event);
        },
        [onInputKeyDown, onKeyDown]
    );

    return (
        <>
            <div ref={inputContainerRef} className={styles.suggestInputContainer}>
                {renderTrigger({
                    component: InputComponent,
                    props: {
                        ...inputProps,
                        value: actualInputValue,
                        onFocus: onFocusHandler,
                        onChange: onChangeHandler,
                        onKeyDown: onKeyDownHandler,
                        ref: inputRefMulti,
                        wrapperRef,
                        autoComplete: 'off',
                    },
                    optionsCount: getSearchResultCount(requestResult?.result),
                } as unknown as InputProp<T> & OptionsCount)}
            </div>
            <SuggestPicker
                data={requestResult?.result}
                activatorRef={wrapperRef}
                inputRef={inputRef}
                inputFocused={focused}
                currentInputValueSelectedFromSuggest={valueSelectedFromSuggest !== false}
                onValueSelect={onValueSelectHandler}
                maxHeight={maxHeight}
                navigationBarProps={navigationBarProps}
                suggestContainerRef={suggestContainerRef}
                itemsGap={itemsGap}
                onBottomSheetClose={handleBottomSheetClose}
                onBottomSheetOpen={onBottomSheetOpen}
                onDropOpen={onDropOpen}
                onDropClose={onDropClose}
                inputValue={actualInputValue}
                topContent={topContent}
                bottomContent={bottomContent}
                dropWidth={dropWidth}
                isError={isError}
                trls={trls}
                errorPlaceholder={errorPlaceholder}
                onClickReload={() => {
                    requestData(actualInputValue);
                }}
                onSearchSubmit={onSearchSubmit}
                input={
                    {
                        component: InputComponent,
                        props: {
                            ...input.props,
                            onFocus: onFocusHandler,
                            onChange: onChangeHandler,
                            value: actualInputValue,
                            onKeyDown: onKeyDownHandler,
                        },
                    } as InputProp<T>
                }
            />
        </>
    );
};

const Suggest = forwardRef(SuggestComponent) as (<K, T extends SuggestInputComponent>(
    props: SuggestProps<K, T> & { ref?: ForwardedRef<HTMLElement> }
) => ReactElement | null) & { displayName: string };

Suggest.displayName = 'Suggest';

export { Suggest };
